import React, { useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { CheckCircle2, Lock, Mail, Clock, ShieldCheck, ArrowRight } from 'lucide-react';
function useCountdown(hours: number) {
  const [timeLeft, setTimeLeft] = useState(hours * 3600);
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(t => t > 0 ? t - 1 : 0);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const h = Math.floor(timeLeft / 3600);
  const m = Math.floor(timeLeft % 3600 / 60);
  const s = timeLeft % 60;
  return {
    h,
    m,
    s
  };
}
function TimeUnit({
  value,
  label
}: {
  value: number;
  label: string;
}) {
  return <div className="flex flex-col items-center" data-id="element-134">
      <div className="bg-white/20 rounded-lg w-14 h-14 flex items-center justify-center text-2xl font-bold text-white tabular-nums" data-id="element-135">
        {String(value).padStart(2, '0')}
      </div>
      <span className="text-xs text-slate-400 mt-1 uppercase tracking-wide" data-id="element-136">
        {label}
      </span>
    </div>;
}
export function ClosingSection() {
  const {
    h,
    m,
    s
  } = useCountdown(24);
  return <section className="bg-white py-16 sm:py-24" data-id="element-137">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-138">
        <div className="mx-auto max-w-2xl" data-id="element-139">
          {/* Closing copy */}
          <div className="mb-14" data-id="element-140">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-serif mb-8 leading-tight" data-id="element-141">
              "Tu paz no es un gasto,
              <br className="hidden sm:block" data-id="element-142" /> es tu mejor inversión"
            </h2>

            <div className="space-y-5 text-lg text-slate-600 leading-relaxed" data-id="element-143">
              <p data-id="element-144">
                Mirá, podés seguir intentando{' '}
                <strong className="text-slate-800" data-id="element-145">"aguantar"</strong>. Podés
                seguir esperando que el nudo en la garganta se vaya solo, o que
                mañana mágicamente te levantes sin esa opresión en el pecho.
              </p>
              <p data-id="element-146">
                Pero las dos sabemos que{' '}
                <strong className="text-slate-800" data-id="element-147">
                  la ansiedad no pide permiso
                </strong>
                , se queda y se acumula.
              </p>

              <p className="font-semibold text-slate-800 text-xl pt-2" data-id="element-148">
                Hoy tenés dos caminos:
              </p>

              <div className="space-y-4 pl-4" data-id="element-149">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100" data-id="element-150">
                  <span className="text-xl mt-0.5" data-id="element-151">😔</span>
                  <p className="text-slate-700" data-id="element-152">
                    Seguir como hasta ahora, dejando que el estrés decida cómo
                    va a ser tu día, tu noche y tu humor con los que amás.
                  </p>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-100" data-id="element-153">
                  <span className="text-xl mt-0.5" data-id="element-154">✨</span>
                  <p className="text-slate-700" data-id="element-155">
                    O{' '}
                    <strong className="text-blue-700" data-id="element-156">
                      invertir hoy menos de lo que cuesta una salida a comer
                    </strong>
                    , para tener en tu celular el "botiquín de emergencia" que
                    te va a devolver el aire cada vez que lo necesites.
                  </p>
                </div>
              </div>

              <p data-id="element-157">
                No es un gasto. Es comprarte{' '}
                <strong className="text-slate-800" data-id="element-158">5 minutos de paz</strong>{' '}
                cada vez que el mundo se ponga difícil. Es recuperar tu eje en
                el bondi, en la oficina o antes de dormir.
              </p>

              <p className="text-xl font-serif text-slate-800 italic border-l-4 border-blue-400 pl-4 py-1" data-id="element-159">
                "Hacé clic abajo, llevate el Kit SOS + la Audio-Guía de Rescate
                y empezá a respirar de verdad. Te espero del otro lado."
              </p>
            </div>
          </div>

          {/* Countdown */}
          <div className="bg-slate-900 rounded-2xl p-6 mb-8 text-center" data-id="element-160">
            <div className="flex items-center justify-center gap-2 mb-3" data-id="element-161">
              <Clock className="w-4 h-4 text-amber-400" data-id="element-162" />
              <p className="text-amber-400 font-semibold text-sm uppercase tracking-wide" data-id="element-163">
                Precio promocional de lanzamiento — válido por las próximas 24hs
              </p>
            </div>
            <div className="flex items-center justify-center gap-3" data-id="element-164">
              <TimeUnit value={h} label="horas" data-id="element-165" />
              <span className="text-white text-2xl font-bold mb-4" data-id="element-166">:</span>
              <TimeUnit value={m} label="min" data-id="element-167" />
              <span className="text-white text-2xl font-bold mb-4" data-id="element-168">:</span>
              <TimeUnit value={s} label="seg" data-id="element-169" />
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-center shadow-2xl shadow-blue-500/20" data-id="element-170">
            <p className="text-blue-100 text-sm font-medium mb-2 uppercase tracking-wide" data-id="element-171">
              Pago único
            </p>
            <div className="flex items-baseline justify-center gap-3 mb-6" data-id="element-172">
              <span className="text-slate-300 line-through text-xl" data-id="element-173">
                AR$ 15.000
              </span>
              <span className="text-5xl font-bold text-white" data-id="element-174">AR$ 8.900</span>
            </div>

            <a href="#oferta-cta" onClick={e => {
            e.preventDefault();
            document.getElementById('oferta-cta')?.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }} className="inline-flex items-center justify-center rounded-lg font-bold transition-colors bg-white text-blue-700 hover:bg-blue-50 shadow-lg h-14 px-10 text-base sm:text-lg w-full" data-id="element-175">
              SÍ, QUIERO MI KIT SOS Y RECUPERAR MI CALMA AHORA
              <ArrowRight className="ml-2 h-5 w-5 flex-shrink-0" data-id="element-176" />
            </a>

            <div className="flex items-center justify-center gap-2 mt-4 text-blue-200 text-sm" data-id="element-177">
              <Lock className="w-3.5 h-3.5" data-id="element-178" />
              <span data-id="element-179">Pago único y seguro vía Mercado Pago</span>
            </div>

            {/* Delivery note */}
            <div className="flex items-center justify-center gap-2 mt-2 text-blue-200 text-sm" data-id="element-180">
              <Mail className="w-3.5 h-3.5" data-id="element-181" />
              <span data-id="element-182">
                Recibís el link de descarga en tu email 1 minuto después del
                pago
              </span>
            </div>
          </div>

          {/* Guarantee */}
          <div className="mt-8 flex items-start gap-4 p-6 rounded-xl bg-green-50 border border-green-100" data-id="element-183">
            <ShieldCheck className="w-8 h-8 text-green-600 flex-shrink-0 mt-0.5" data-id="element-184" />
            <div data-id="element-185">
              <p className="font-bold text-slate-900 mb-1" data-id="element-186">
                Garantía de Satisfacción de 7 días
              </p>
              <p className="text-slate-600 text-sm leading-relaxed" data-id="element-187">
                Si en 7 días sentís que estas técnicas no son para vos, te
                devolvemos el 100% de tu dinero sin preguntas. Tu paz es lo
                primero.
              </p>
            </div>
          </div>

          {/* Trust badges */}
          <div className="mt-6 grid grid-cols-3 gap-4 text-center" data-id="element-188">
            <div className="flex flex-col items-center gap-1" data-id="element-189">
              <Lock className="w-5 h-5 text-slate-400" data-id="element-190" />
              <span className="text-xs text-slate-500" data-id="element-191">Pago seguro</span>
            </div>
            <div className="flex flex-col items-center gap-1" data-id="element-192">
              <Mail className="w-5 h-5 text-slate-400" data-id="element-193" />
              <span className="text-xs text-slate-500" data-id="element-194">Acceso inmediato</span>
            </div>
            <div className="flex flex-col items-center gap-1" data-id="element-195">
              <CheckCircle2 className="w-5 h-5 text-slate-400" data-id="element-196" />
              <span className="text-xs text-slate-500" data-id="element-197">Producto digital</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}